'use client';

/**
 * useLocaleSwitcher - locale chip metadata for the header/footer switchers.
 *
 * Simplified from the prostay.com version: this site has no blog, so every
 * route exists in every locale and the slug-mapping machinery is gone. The
 * rendering contract is unchanged:
 *   - active chips render as <span aria-current="page">
 *   - other chips render as a plain next/link <Link href={href}>
 *
 * We do NOT use next-intl's locale-aware <Link> for the chips: from a
 * non-default-locale page with locale="en" it emits `/en/...` despite
 * `localePrefix: 'as-needed'`, creating a 308 redirect chain to the
 * unprefixed canonical. Constructing the prefix here keeps the href
 * identical to the URL the visitor actually lands on.
 */

import {useMemo} from 'react';
import {useLocale} from 'next-intl';
import {usePathname} from '@/i18n/navigation';

const LOCALE_OPTIONS = [
  {code: 'en', label: 'English'},
  {code: 'es', label: 'Español'},
  {code: 'de', label: 'Deutsch'},
  {code: 'fr', label: 'Français'},
  {code: 'it', label: 'Italiano'}
];

export function useLocaleSwitcher({onSelect} = {}) {
  const locale = useLocale();
  const pathname = usePathname();

  return useMemo(
    () =>
      LOCALE_OPTIONS.map((opt) => {
        const active = opt.code === locale;
        const handler = () => {
          if (active) return;
          if (typeof onSelect === 'function') {
            onSelect();
          }
        };
        const path = pathname || '/';
        const href = opt.code === 'en' ? path : `/${opt.code}${path}`;
        return {
          code: opt.code,
          label: opt.label,
          active,
          disabled: false,
          href,
          onSelect: handler
        };
      }),
    [locale, pathname, onSelect]
  );
}

export const SWITCHER_LOCALES = LOCALE_OPTIONS;
